/**
 * @import { ItineraryDay } from '@/features/itinerary-map/types/itinerary-day'
 */

import { getItineraryLearnMoreTrackingAttributes } from '@/features/itinerary-map/utils/analytics';
import { formatPortName } from '@/features/itinerary-map/utils/cruise';
import { Popup } from '@/ui/popup';
import { useCallback, useEffect } from 'react';
import { ArrowIcon } from '../arrow-icon';
import classes from './port-info.module.css';

/**
 * @param {object} props
 * @param {ItineraryDay[]} props.days
 * @param {number} props.selectedDayIndex
 * @param {google.maps.marker.AdvancedMarkerElement} props.anchor
 * @param {() => void} [props.onClose]
 * @param {(index: number) => void} props.onChangeSelectedDayIndex
 * @param {(dayNumber: string) => void} props.onSelectDay
 * @returns {React.JSX.Element | undefined}
 */
export function PortInfo({ days, selectedDayIndex, anchor, onClose, onChangeSelectedDayIndex, onSelectDay }) {
  const selectedDay = days[selectedDayIndex];
  const analyticsDayName = formatPortName(selectedDay?.port);
  const name = selectedDay.port.name;
  const isMultiDay = days.length > 1;

  const autoFocus = useCallback((/** @type {HTMLButtonElement} */ element) => {
    element?.focus();
  }, []);

  // Close automatically on map click or bounds change
  useEffect(() => {
    const listeners = ['click', 'bounds_changed'].map((eventName) => {
      return anchor.map?.addListener(eventName, () => {
        onClose?.();
      });
    });

    return () => {
      listeners.forEach((listener) => listener?.remove());
    };
  }, [anchor.map, onClose]);

  return (
    <Popup anchor={anchor} className={classes.portInfo} ariaLabel={name}>
      <button ref={autoFocus} type="button" className={classes.btnClose} onClick={onClose} aria-label="close">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="10" fill="black" fillOpacity="0.5" />
          <path
            d="M5.5 15.625L4.375 14.5L8.875 10L4.375 5.5L5.5 4.375L10 8.875L14.5 4.375L15.625 5.5L11.125 10L15.625 14.5L14.5 15.625L10 11.125L5.5 15.625Z"
            fill="white"
          />
        </svg>
      </button>
      <div className={classes.dayList} style={{ transform: `translateX(${selectedDayIndex * -100}%)` }}>
        {days.map((d, index) => {
          const dayNumber = d.dayNumber;
          const hasNext = index < days.length - 1;
          const hasPrev = index > 0;
          const hidden = index !== selectedDayIndex;
          return (
            <div
              key={index}
              className={classes.dayListItem}
              style={{ backgroundImage: `url(${d.port.image.url})` }}
              aria-hidden={hidden}
            >
              {isMultiDay && (
                <div className={classes.dayNav}>
                  {hasPrev && (
                    <button
                      type="button"
                      aria-label="previous day"
                      className={classes.btnPrev}
                      onClick={() => onChangeSelectedDayIndex(index - 1)}
                      disabled={hidden}
                    >
                      <ArrowIcon style={{ transform: 'rotate(180deg)' }} />
                    </button>
                  )}

                  {hasNext && (
                    <button
                      type="button"
                      aria-label="next day"
                      className={classes.btnNext}
                      onClick={() => onChangeSelectedDayIndex(index + 1)}
                      disabled={hidden}
                    >
                      <ArrowIcon />
                    </button>
                  )}
                </div>
              )}

              <div className={classes.content}>
                <div className={classes.detail}>
                  <span>
                    Day {dayNumber}: {name}
                  </span>
                  <button
                    type="button"
                    className={classes.btnLearnMore}
                    onClick={() => onSelectDay(dayNumber)}
                    {...getItineraryLearnMoreTrackingAttributes(analyticsDayName)}
                    disabled={hidden}
                  >
                    Learn More
                  </button>
                </div>
                <div className={classes.country}>{d.port.country}</div>
                {isMultiDay && (
                  <div className={classes.info}>This destination is included on several days of your cruise.</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Popup>
  );
}
